import React, { useState, useEffect } from 'react';

/**
 * QuestionDimensional Component
 * Used for: DISC, MBTI
 * Format: MOST-LEAST selection (select 2 from 4 options)
 */
const QuestionDimensional = ({
    question,
    answer,
    onAnswer,
    disabled = false
}) => {
    const [selectedAnswers, setSelectedAnswers] = useState(answer || { most: null, least: null });

    useEffect(() => {
        setSelectedAnswers(answer || { most: null, least: null });
    }, [answer]);

    const handleAnswer = (type, value) => {
        if (disabled) return;

        const newAnswers = { ...selectedAnswers, [type]: value };

        // Prevent selecting same option for both most and least
        if (type === 'most' && value === selectedAnswers.least) {
            newAnswers.least = null;
        }
        if (type === 'least' && value === selectedAnswers.most) {
            newAnswers.most = null;
        }

        setSelectedAnswers(newAnswers);
        onAnswer(question.id, newAnswers);
    };

    const isDisabled = (optionLabel, type) => {
        if (disabled) return true;
        if (type === 'most') return selectedAnswers.least === optionLabel;
        if (type === 'least') return selectedAnswers.most === optionLabel;
        return false;
    };

    return (
        <div className="question-dimensional">
            {/* Question Text */}
            <div className="question-text mb-4">
                <div
                    className="text-lg font-semibold"
                    dangerouslySetInnerHTML={{ __html: question.soal }}
                />
            </div>

            {/* Image Display */}
            {question.image_path && (
                <div className="question-image mb-4">
                    <img
                        src={`/storage/${question.image_path}`}
                        alt="Question illustration"
                        className="max-w-full h-auto rounded-lg shadow-md"
                        loading="lazy"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}

            {/* Instructions */}
            <div className="instructions mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-sm font-medium text-yellow-800">
                    <strong>Instruksi:</strong> Pilih satu pernyataan yang <strong>PALING</strong> menggambarkan diri Anda
                    dan satu yang <strong>PALING TIDAK</strong> menggambarkan diri Anda
                </p>
            </div>

            {/* Answer Grid */}
            <div className="answer-grid">
                {/* MOST Column */}
                <div className="most-section mb-6">
                    <h3 className="text-md font-bold mb-3 text-green-700">
                        PALING Menggambarkan Saya
                    </h3>
                    <div className="space-y-2">
                        {question.pilihan && question.pilihan.map((option, index) => {
                            const optionLabel = String.fromCharCode(65 + index);
                            const isSelected = selectedAnswers.most === optionLabel;
                            const isOptionDisabled = isDisabled(optionLabel, 'most');

                            return (
                                <div
                                    key={`most-${index}`}
                                    className={`answer-option p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-300 hover:border-green-300 hover:bg-gray-50'
                                        } ${isOptionDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                    onClick={() => !isOptionDisabled && handleAnswer('most', optionLabel)}
                                >
                                    <div className="flex items-center">
                                        <div className={`option-label flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 ${isSelected
                                                ? 'bg-green-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                            }`}>
                                            {optionLabel}
                                        </div>
                                        <div
                                            className="option-text text-sm flex-grow"
                                            dangerouslySetInnerHTML={{ __html: option }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* LEAST Column */}
                <div className="least-section">
                    <h3 className="text-md font-bold mb-3 text-red-700">
                        PALING TIDAK Menggambarkan Saya
                    </h3>
                    <div className="space-y-2">
                        {question.pilihan && question.pilihan.map((option, index) => {
                            const optionLabel = String.fromCharCode(65 + index);
                            const isSelected = selectedAnswers.least === optionLabel;
                            const isOptionDisabled = isDisabled(optionLabel, 'least');

                            return (
                                <div
                                    key={`least-${index}`}
                                    className={`answer-option p-3 rounded-lg border-2 cursor-pointer transition-all ${isSelected
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 hover:border-red-300 hover:bg-gray-50'
                                        } ${isOptionDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                    onClick={() => !isOptionDisabled && handleAnswer('least', optionLabel)}
                                >
                                    <div className="flex items-center">
                                        <div className={`option-label flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold mr-3 ${isSelected
                                                ? 'bg-red-500 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                            }`}>
                                            {optionLabel}
                                        </div>
                                        <div
                                            className="option-text text-sm flex-grow"
                                            dangerouslySetInnerHTML={{ __html: option }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Validation Warning */}
            {(!selectedAnswers.most || !selectedAnswers.least) && (
                <div className="validation-warning mt-4 p-3 bg-orange-50 border-l-4 border-orange-400 rounded">
                    <p className="text-sm text-orange-800">
                        ⚠️ Anda harus memilih kedua pilihan (PALING dan PALING TIDAK) sebelum melanjutkan
                    </p>
                </div>
            )}
        </div>
    );
};

export default QuestionDimensional;
